'use client'

import { ReactNode, MouseEvent, useState } from 'react'
import { Button, Cell, Dialog, Image, Spacer, Text } from '@vinted/web-ui'

import { UiState } from '@marketplace-web/shared/ui-state'
import { useAsset } from '@marketplace-web/domain/assets'
import { useCatalogContext } from '@marketplace-web/domain/catalog-page-data'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'

import { ReportReasonCatalogSystem } from 'constants/report-reason'
import { checkIsAlertsGeneratorBatchBusy } from 'data/api/check-is-alerts-generator-busy'
import { generateAlertsFromFirstPage } from 'data/api/generate-alerts-from-search-params'

import ReportReasonList from './ReportReasonList'

const Title = ({ title }: { title: string }) => (
  <Text
    as="h1"
    type={Text.Type.Heading}
    text={title}
    width={Text.Width.Parent}
    alignment={Text.Alignment.Center}
  />
)

const IconImage = ({ name }: { name: string }) => {
  const asset = useAsset()

  return (
    <div className="u-flexbox u-justify-content-center">
      <Image
        src={asset(`${name}.svg`, { theme: { dark: `${name}-dark.svg` } })}
        size={Image.Size.Large}
      />
    </div>
  )
}

const CloseButton = ({ text, onClose }: { onClose: () => void; text: string }) => (
  <Button text={text} onClick={onClose} styling={Button.Styling.Flat} />
)

const ModalSection = ({ children }: { children: Array<ReactNode> | ReactNode }) => {
  if (Array.isArray(children)) {
    return (
      <Cell>
        <SeparatedList separator={<Spacer size={Spacer.Size.Large} />}>{children}</SeparatedList>
      </Cell>
    )
  }

  return <Cell>{children}</Cell>
}

const SendFirstPageToAlerts = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [modalUiState, setModalUiState] = useState(UiState.Idle)
  const [sendUiState, setSendUiState] = useState(UiState.Idle)
  const [selectedReportReason, setSelectedReportReason] = useState<ReportReasonCatalogSystem>()
  const [isSubmitAttempted, setIsSubmitAttempted] = useState(false)

  const {
    state: {
      items: { items = [], uiState },
    },
  } = useCatalogContext()

  const checkAlertsGenerationStatus = async () => {
    setModalUiState(UiState.Pending)

    const response = await checkIsAlertsGeneratorBatchBusy()

    if ('errors' in response || response.is_busy) {
      setModalUiState(UiState.Failure)

      return
    }

    setModalUiState(UiState.Success)
  }

  const handleSendToAlerts = async (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    if (sendUiState === UiState.Pending) {
      event.preventDefault()

      return
    }

    setIsSubmitAttempted(true)

    if (!selectedReportReason) return

    setSendUiState(UiState.Pending)

    const itemIds = items.map(item => item.id)

    const response = await generateAlertsFromFirstPage({
      itemIds,
      reportReason: selectedReportReason,
    })

    if ('errors' in response) {
      setSendUiState(UiState.Failure)

      return
    }

    setSendUiState(UiState.Success)
    setSelectedReportReason(undefined)
  }

  const handleReportReasonSelect = (value: ReportReasonCatalogSystem) => {
    setSelectedReportReason(value)
  }

  const handleOpenModal = async (
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => {
    if (modalUiState === UiState.Pending) {
      event.preventDefault()

      return
    }

    await checkAlertsGenerationStatus()

    setIsOpen(true)
  }

  const handleCloseModal = () => {
    setIsOpen(false)
    setSendUiState(UiState.Idle)
    setSelectedReportReason(undefined)
    setIsSubmitAttempted(false)
  }

  const renderSendAlertsPrompt = () => (
    <>
      <ModalSection>
        <Title title="Create and send alerts?" />
        <Text as="span" text="You're about to send first page of items to alerts." />
        <Text
          as="span"
          text="Creating and sending alerts can take some time. You won't be able to send new ones immediately"
        />
      </ModalSection>
      <ReportReasonList
        selectedValue={selectedReportReason}
        onSelect={handleReportReasonSelect}
        isSubmitAttempted={isSubmitAttempted}
      />
      <ModalSection>
        <Button
          text="Send"
          onClick={handleSendToAlerts}
          styling={Button.Styling.Filled}
          isLoading={sendUiState === UiState.Pending}
        />
        <CloseButton onClose={handleCloseModal} text="Close" />
      </ModalSection>
    </>
  )

  const renderAlertsSent = () => (
    <>
      <ModalSection>
        <IconImage name="check-circle" />
        <Title title="Listings were sent" />
        <Text
          as="span"
          text="The system has started to work on generating alerts. It will take some time to create all alerts"
        />
      </ModalSection>
      <ModalSection>
        <CloseButton onClose={handleCloseModal} text="Close" />
      </ModalSection>
    </>
  )

  const renderGenerationPendingError = () => (
    <>
      <ModalSection>
        <IconImage name="clock" />
        <Title title="System is already generating alerts" />
        <Text as="span" text="Please wait till all alerts are generated and try again" />
      </ModalSection>
      <ModalSection>
        <CloseButton onClose={handleCloseModal} text="Close" />
      </ModalSection>
    </>
  )

  const renderUnknownError = () => (
    <>
      <ModalSection>
        <Title title="Listings weren’t sent to alerts" />
        <Text
          as="span"
          text="An unknown error occurred while trying to send listings to alerts. Please try again"
        />
      </ModalSection>
      <ModalSection>
        <CloseButton onClose={handleCloseModal} text="Close" />
      </ModalSection>
    </>
  )

  const renderModalContent = () => {
    if (modalUiState === UiState.Failure) return renderGenerationPendingError()

    if (sendUiState === UiState.Success) return renderAlertsSent()

    if (sendUiState === UiState.Failure) return renderUnknownError()

    return renderSendAlertsPrompt()
  }

  return (
    <>
      <Button
        text="Send first page to alerts"
        onClick={handleOpenModal}
        size={Button.Size.Small}
        styling={Button.Styling.Filled}
        isLoading={modalUiState === UiState.Pending || uiState === UiState.Pending}
        inline
      />
      <Dialog show={isOpen}>{renderModalContent()}</Dialog>
    </>
  )
}

export default SendFirstPageToAlerts
